import type { EmissionRecordView } from "../../../../../../apiClient/generated";
import { groupBridgerEmissions, type SiteTotal } from "./bridger";

// Providers reporting 24-hour averages instead of single detections.
export const DAILY_AVERAGE_PROVIDER_NAMES = new Set(["Longpath"]);

type Base = {
    id: string;
    x: Date;
    xEnd: Date;
    records: EmissionRecordView[];
};

export type PlotEmissionMark =
    | (Base & {
          kind: "point";
          record: EmissionRecordView;
          y: number;
          inGroup: boolean;
      })
    | (Base & { kind: "dailyAverage"; record: EmissionRecordView; y: number })
    | (Base & { kind: "concentration"; record: EmissionRecordView })
    | (Base & { kind: "groupedEmissions"; y: number; siteTotal: SiteTotal });

export const emissionsToMarks = (
    emissionRecords: EmissionRecordView[],
): PlotEmissionMark[] => {
    const [emissions, groupedSiteTotals] = groupBridgerEmissions(
        emissionRecords,
        true,
    );
    const marks: PlotEmissionMark[] = [];

    // Figure out the grouped emissions.
    const idsInGroups = new Set(
        Object.values(groupedSiteTotals).flatMap((v) =>
            v.records.map((r) => r.id),
        ),
    );

    // Add in normal emissions
    for (const e of emissions) {
        const base = {
            id: e.id,
            x: e.dataPoint.detectionTimestamp,
            xEnd: e.dataPoint.detectionTimestamp,
            record: e,
            records: [e],
        };
        const rate = e.dataPoint.detectedRate;

        if (!rate && e.dataPoint.concentration != null) {
            marks.push({
                ...base,
                kind: "concentration",
            });
        } else if (rate == null) {
        } else if (DAILY_AVERAGE_PROVIDER_NAMES.has(e.providerName)) {
            marks.push({
                ...base,
                kind: "dailyAverage",
                y: rate,
                xEnd: new Date(base.x.getTime() + 24 * 60 * 60 * 1000),
            });
        } else {
            marks.push({
                ...base,
                kind: "point",
                y: rate,
                inGroup: idsInGroups.has(e.id),
            });
        }
    }

    for (const [key, siteTotal] of Object.entries(groupedSiteTotals)) {
        marks.push({
            id: `group:${key}`,
            kind: "groupedEmissions",
            x: siteTotal.meanDate ?? new Date(siteTotal.date),
            xEnd: siteTotal.meanDate ?? new Date(siteTotal.date),
            y: siteTotal.total,
            records: siteTotal.records,
            siteTotal,
        });
    }

    return marks;
};

/**
 * Mean detected rate (kg/h) of the site-level observations.
 */
export const computeAverageDetectedRate = (
    records: EmissionRecordView[],
): number | undefined => {
    const values: number[] = [];
    for (const m of emissionsToMarks(records)) {
        if (m.kind === "concentration" || (m.kind === "point" && m.inGroup)) {
            continue;
        }
        if (m.y > 0) {
            values.push(m.y);
        }
    }

    if (!values.length) {
        return undefined;
    }

    return values.reduce((sum, value) => sum + value, 0) / values.length / 1000;
};
